
import React, { useState, useEffect, useMemo } from 'react';
import { Reservation, ReservationSource, AppSettings, Table } from '../types';
import { SOURCE_LABELS } from '../constants';
import { getHolidaysForYear } from '../services/holidayService';
import { Calendar as CalendarIcon, Clock, Users, Plus, Phone, Store, X, Check, Edit2, ChevronLeft, ChevronRight, AlertTriangle, Search, RotateCcw, Trash2, AlignLeft, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeNumberInput, useLongPress } from './MobileControls';

interface ReservationViewProps {
  reservations: Reservation[];
  appSettings: AppSettings;
  tables: Table[];
  onAddReservation: (res: Reservation) => void;
  onUpdateReservation: (res: Reservation) => void;
  onUpdateStatus: (id: string, status: Reservation['status']) => void;
  onJumpToSchedule: (date: string) => void;
}

const CustomInstagramIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
  </svg>
);

const CustomLineIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
  </svg>
);

const SourceIcon: React.FC<{ source: ReservationSource }> = ({ source }) => {
  switch (source) {
    case 'instagram': return <CustomInstagramIcon size={16} />;
    case 'line': return <CustomLineIcon size={16} />;
    case 'phone': return <Phone size={16} />;
    case 'store': return <Store size={16} />;
    default: return <Store size={16} />;
  }
};

interface CalendarDayCellProps {
  day: number;
  month: number;
  year: number;
  reservations: Reservation[];
  isHoliday: boolean;
  holidayName?: string;
  isClosed: boolean;
  isSelected: boolean;
  onSelect: (dateStr: string) => void;
  onAction: (dateStr: string) => void;
}

const CalendarDayCell: React.FC<CalendarDayCellProps> = ({
  day, month, year, reservations, isHoliday, holidayName, isClosed, isSelected, onSelect, onAction
}) => {
  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const longPressHandlers = useLongPress(() => onAction(dateStr));
  const hasReservation = reservations.some(r => r.date === dateStr && r.status !== 'cancelled');
  const hasUnassigned = reservations.some(r => r.date === dateStr && r.status !== 'cancelled' && !r.tableId);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    onAction(dateStr);
  };

  return (
    <div
      {...longPressHandlers}
      onClick={() => onSelect(dateStr)}
      onContextMenu={handleContextMenu}
      className={`
        h-14 rounded-xl border flex flex-col items-center justify-center relative cursor-pointer transition-all select-none
        ${isSelected
          ? 'bg-stone-800 text-white border-stone-800 shadow-md transform scale-105 z-10'
          : isClosed
            ? 'bg-stone-100 text-stone-300 border-transparent cursor-not-allowed'
            : 'bg-white border-stone-100 hover:border-orange-200 text-stone-600'
        }
      `}
    >
      <span className={`text-sm font-bold ${isHoliday && !isSelected ? 'text-red-500' : ''}`}>
        {day}
      </span>
      {holidayName && !isSelected && (
        <span className="text-[8px] text-red-400 leading-none text-center px-1 truncate w-full absolute top-1">{holidayName}</span>
      )}
      {hasReservation && (
        <div className="flex gap-0.5 mt-1">
          <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-orange-400' : 'bg-orange-300'}`}></span>
          {hasUnassigned && <span className={`w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse`}></span>}
        </div>
      )}
    </div>
  );
};

const ReservationCard: React.FC<{ res: Reservation, tables: Table[], onEdit: (res: Reservation) => void, onUpdateStatus: (id: string, status: Reservation['status']) => void, showDate?: boolean }> = ({ res, tables, onEdit, onUpdateStatus, showDate }) => {
  const isCancelled = res.status === 'cancelled';
  const isUnassigned = !res.tableId;
  const label = SOURCE_LABELS[res.source];
  const table = tables.find(t => t.id === res.tableId);
  const longPressHandlers = useLongPress(() => confirm('削除しますか？') && onUpdateStatus(res.id, 'cancelled'));

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        p-4 rounded-2xl border flex gap-4 items-center cursor-pointer transition-all mx-1
        ${isCancelled ? 'bg-stone-50 border-stone-100 opacity-60' : 
          isUnassigned ? 'bg-amber-50/50 border-amber-200 shadow-inner' : 'bg-white border-stone-100 hover:border-orange-200 shadow-sm'}
      `}
      onClick={() => onEdit(res)}
      {...longPressHandlers}
    >
      <div className="flex-1 min-w-0">
        {showDate && <div className="text-[10px] font-bold text-orange-500 mb-1">{res.date}</div>}
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-bold text-lg text-cafe-text truncate max-w-[150px]">{res.name} 様</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1 ${label.color}`}><SourceIcon source={res.source} />{label.label}</span>
          {isUnassigned && !isCancelled && (
             <span className="text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-md font-black animate-pulse flex items-center gap-0.5 shrink-0">
               <AlertCircle size={10} /> 未決定
             </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-stone-500">
          <span className="flex items-center gap-1 font-mono"><Clock size={12}/> {res.time}</span>
          <span className="flex items-center gap-1"><Users size={12}/> {res.guests}名</span>
          {table && <span className="bg-stone-100 px-1.5 rounded text-[10px] font-bold border border-stone-200 text-stone-500">{table.name}</span>}
        </div>
        {res.notes && (
          <div className="mt-2 flex items-start gap-1 text-[10px] text-stone-400 italic line-clamp-1 border-t border-stone-50 pt-1">
             <AlignLeft size={10} className="shrink-0 mt-0.5" />
             {res.notes}
          </div>
        )}
      </div>
      {!isCancelled && <button onClick={(e) => { e.stopPropagation(); onEdit(res); }} className={`p-2 transition-colors shrink-0 ${isUnassigned ? 'text-amber-400 hover:text-amber-600' : 'text-stone-300 hover:text-stone-500'}`}><Edit2 size={18} /></button>}
    </motion.div>
  );
};

export const ReservationView: React.FC<ReservationViewProps> = ({ reservations, appSettings, tables, onAddReservation, onUpdateReservation, onUpdateStatus, onJumpToSchedule }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formState, setFormState] = useState<Reservation>({ id: '', name: '', date: '', time: '', guests: 2, source: 'instagram', notes: '', status: 'confirmed', tableId: '' });
  const [formHour, setFormHour] = useState(12);
  const [formMinute, setFormMinute] = useState(0);

  const holidays = useMemo(() => getHolidaysForYear(currentMonth.getFullYear()), [currentMonth.getFullYear()]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay, year, month };
  };

  const { days, firstDay, year, month } = getDaysInMonth(currentMonth);
  const isDayClosed = (day: number) => {
    const date = new Date(year, month, day);
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (appSettings.openDates?.includes(dateStr)) return false;
    if (appSettings.closedDates?.includes(dateStr)) return true;
    if (appSettings.closedDays?.includes(date.getDay())) return true;
    return false;
  };

  const selectedDateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

  const searchResults = useMemo(() => {
    if (!searchQuery) return [];
    return reservations
      .filter(r => r.name.toLowerCase().includes(searchQuery.toLowerCase()) && r.status !== 'cancelled')
      .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));
  }, [reservations, searchQuery]);

  const filteredReservations = reservations
    .filter(r => r.date === selectedDateStr && r.status !== 'cancelled')
    .sort((a, b) => a.time.localeCompare(b.time));

  const openAddModal = (dateOverride?: string) => {
    setFormState({ id: '', name: '', date: dateOverride || selectedDateStr, time: '', guests: 2, source: 'instagram', notes: '', status: 'confirmed', tableId: '' });
    setFormHour(new Date().getHours()); 
    setFormMinute(0); 
    setIsAddModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formattedTime = `${formHour.toString().padStart(2, '0')}:${formMinute.toString().padStart(2, '0')}`;
    const resData = { ...formState, time: formattedTime };
    if (resData.id) onUpdateReservation(resData); else onAddReservation({ ...resData, id: Date.now().toString() });
    setIsAddModalOpen(false); setIsEditModalOpen(false);
  };

  const sources: ReservationSource[] = ['instagram', 'line', 'phone', 'store'];

  return (
    <div className="h-full flex flex-col md:flex-row gap-4 overflow-hidden overscroll-none w-full">
      <div className="flex-1 flex flex-col h-1/2 md:h-full bg-white rounded-3xl shadow-sm border border-stone-100 p-4 overflow-hidden min-w-0">
        <div className="relative mb-4">
           <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-300" size={16} />
           <input 
             type="text"
             placeholder="予約者名で検索..."
             className="w-full bg-stone-50 border-none rounded-xl py-2.5 pl-10 pr-4 text-sm font-bold focus:ring-2 focus:ring-orange-100 outline-none"
             value={searchQuery}
             onChange={e => setSearchQuery(e.target.value)}
           />
           {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-300"><X size={16}/></button>}
        </div>

        <div className="flex justify-between items-center mb-4 shrink-0">
          <div className="flex items-center gap-2">
             <button onClick={() => setCurrentMonth(new Date(year, month - 1))} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><ChevronLeft size={20}/></button>
             <h2 className="text-lg font-bold text-cafe-text min-w-[100px] text-center">{year}年 {month + 1}月</h2>
             <button onClick={() => setCurrentMonth(new Date(year, month + 1))} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><ChevronRight size={20}/></button>
          </div>
          <button onClick={() => { const now = new Date(); setCurrentMonth(now); setSelectedDate(now); }} className="text-xs bg-stone-100 text-stone-600 px-3 py-1.5 rounded-full font-bold hover:bg-stone-200 transition-all">今日</button>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar overscroll-contain">
          <div className="grid grid-cols-7 gap-1 mb-2 text-center">
            {['日', '月', '火', '水', '木', '金', '土'].map((d, i) => <div key={i} className="text-xs font-bold text-stone-400 py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: days }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const holidayName = holidays[dateStr];
              return (
                <CalendarDayCell 
                  key={day} 
                  day={day} 
                  month={month} 
                  year={year} 
                  reservations={reservations} 
                  isHoliday={!!holidayName} 
                  holidayName={holidayName} 
                  isClosed={isDayClosed(day)} 
                  isSelected={dateStr === selectedDateStr} 
                  onSelect={(d) => { const [y, m, dt] = d.split('-').map(Number); setSelectedDate(new Date(y, m - 1, dt)); }} 
                  onAction={onJumpToSchedule} 
                />
              );
            })}
          </div>
          <p className="mt-4 text-[10px] text-stone-400 text-center italic font-bold opacity-70">
            ※ PCは右クリック / スマホは長押し で予定表へ
          </p>
        </div>
      </div>

      <div className="flex-1 flex flex-col h-1/2 md:h-full overflow-hidden min-w-0">
        <div className="flex justify-between items-center mb-4 px-2 shrink-0">
          <h2 className="font-bold text-cafe-text flex items-center gap-2 truncate">
             <span className="w-2 h-6 bg-orange-300 rounded-full shrink-0"></span>
             <span className="truncate">{searchQuery ? `「${searchQuery}」の結果` : `${selectedDate.getMonth() + 1}/${selectedDate.getDate()} の予約`}</span>
             <span className="text-sm font-normal text-stone-400 shrink-0">({searchQuery ? searchResults.length : filteredReservations.length}件)</span>
          </h2>
          <button onClick={() => openAddModal()} className="bg-stone-800 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg flex items-center gap-2 active:scale-95 transition-all shrink-0"><Plus size={16} />新規</button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain space-y-3 pb-24 px-1 no-scrollbar md:pb-6 relative w-full">
          {(searchQuery ? searchResults : filteredReservations).length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-stone-400 opacity-60">
              <CalendarIcon size={48} className="mb-4 text-stone-200" />
              <p>予約はありません</p>
            </div>
          ) : (
            (searchQuery ? searchResults : filteredReservations).map(res => (
              <ReservationCard 
                key={res.id} 
                res={res} 
                tables={tables} 
                onEdit={(r) => { 
                  const [h, m] = r.time.split(':').map(Number); 
                  setFormState(r); 
                  setFormHour(h); 
                  setFormMinute(m); 
                  setIsEditModalOpen(true); 
                }} 
                onUpdateStatus={onUpdateStatus} 
                showDate={!!searchQuery} 
              />
            ))
          )}
        </div>
      </div>

      <AnimatePresence>
        {(isAddModalOpen || isEditModalOpen) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl p-6 max-h-[95vh] flex flex-col">
              <h3 className="font-bold text-lg mb-4 text-center shrink-0">{isEditModalOpen ? '予約情報の変更' : '新規予約'}</h3>
              
              <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto no-scrollbar pr-1 flex-1">
                <div className="flex gap-4">
                   <div className="flex-1 space-y-1">
                      <label className="text-xs font-bold text-stone-500">お名前</label>
                      <input type="text" required className="w-full bg-stone-50 rounded-xl h-12 px-4 text-sm font-bold focus:ring-2 focus:ring-orange-100 outline-none" value={formState.name} onChange={e => setFormState({...formState, name: e.target.value})} />
                   </div>
                   <div className="flex-1">
                      <SwipeNumberInput label="人数" value={formState.guests} onChange={v => setFormState({...formState, guests: v})} min={1} unit="名" />
                   </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-stone-500">予約経路</label>
                  <div className="grid grid-cols-4 gap-2">
                    {sources.map(s => {
                      const label = SOURCE_LABELS[s];
                      const isSelected = formState.source === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setFormState({...formState, source: s})}
                          className={`flex flex-col items-center justify-center py-2 rounded-xl border transition-all ${
                            isSelected 
                              ? 'bg-stone-800 text-white border-stone-800 shadow-md scale-105' 
                              : 'bg-stone-50 border-stone-100 text-stone-400 hover:border-stone-200'
                          }`}
                        >
                          <SourceIcon source={s} />
                          <span className="text-[10px] font-bold mt-1">{label.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-4">
                   <div className="flex-1">
                      <label className="text-xs font-bold text-stone-500">テーブル</label>
                      <select className="w-full bg-stone-50 rounded-xl h-12 px-3 text-sm font-bold focus:ring-2 focus:ring-orange-100 outline-none" value={formState.tableId || ''} onChange={e => setFormState({...formState, tableId: e.target.value})}>
                         <option value="">未定</option>
                         {tables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                   </div>
                   <div className="flex-1">
                      <label className="text-xs font-bold text-stone-500">日付</label>
                      <input type="date" required className="w-full bg-stone-50 rounded-xl h-12 px-3 text-sm font-bold focus:ring-2 focus:ring-orange-100 outline-none" value={formState.date} onChange={e => setFormState({...formState, date: e.target.value})} />
                   </div>
                </div>

                <div className="space-y-1">
                   <label className="text-xs font-bold text-stone-500 block text-center">来店時間</label>
                   <div className="flex gap-2">
                      <div className="flex-1">
                         <SwipeNumberInput label="時" value={formHour} onChange={setFormHour} min={0} max={23} unit="時" />
                      </div>
                      <div className="flex-1">
                         <SwipeNumberInput label="分" value={formMinute} onChange={setFormMinute} min={0} max={59} step={10} format={v=>v.toString().padStart(2,'0')} unit="分" />
                      </div>
                   </div>
                </div>

                <div className="space-y-1">
                   <label className="text-xs font-bold text-stone-500">メモ</label>
                   <textarea className="w-full bg-stone-50 rounded-xl p-3 h-20 resize-none text-sm font-medium focus:ring-2 focus:ring-orange-100 outline-none" value={formState.notes} onChange={e => setFormState({...formState, notes: e.target.value})} />
                </div>
              </form>

              <div className="flex gap-3 pt-4 border-t border-stone-100 mt-4 shrink-0">
                 {isEditModalOpen && <button type="button" onClick={() => confirm('削除しますか？') && (onUpdateStatus(formState.id, 'cancelled'), setIsEditModalOpen(false))} className="px-4 bg-red-50 text-red-500 rounded-xl transition-colors active:scale-90"><Trash2 size={20} /></button>}
                 <button type="button" onClick={() => { setIsAddModalOpen(false); setIsEditModalOpen(false); }} className="flex-1 bg-stone-100 text-stone-500 h-12 rounded-xl font-bold transition-colors active:scale-95">戻る</button>
                 <button type="submit" onClick={handleSubmit} className="flex-[2] bg-stone-800 text-white h-12 rounded-xl font-bold shadow-lg transition-colors active:scale-95">{isEditModalOpen ? '更新する' : '予約する'}</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
